<script setup>
import { ref, computed } from 'vue';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import {
  knowledgeContent,
  formatFileSize,
  titleFromFilename,
} from '../../helpers/kraftchatAIKnowledgeConfig';

defineProps({
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['create']);

const dialogRef = ref(null);
const fileInput = ref(null);

const selectedFile = ref(null);
const title = ref('');
const active = ref(true);
const titleEditedManually = ref(false);

const selectedFileSizeLabel = computed(() =>
  selectedFile.value ? formatFileSize(selectedFile.value.size) : ''
);

const handleFileClick = () => fileInput.value?.click();

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  selectedFile.value = file || null;

  if (file && !titleEditedManually.value) {
    title.value = titleFromFilename(file.name);
  }
};

const handleRemoveFile = () => {
  selectedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = null;
  }
};

const handleTitleInput = value => {
  title.value = value;
  titleEditedManually.value = true;
};

const resetForm = () => {
  selectedFile.value = null;
  title.value = '';
  active.value = true;
  titleEditedManually.value = false;
  if (fileInput.value) fileInput.value.value = null;
};

const handleConfirm = () => {
  if (!selectedFile.value) return;

  emit('create', {
    file: selectedFile.value,
    title: title.value.trim(),
    active: active.value,
  });
};

defineExpose({ dialogRef, resetForm });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="knowledgeContent.pdfDialogTitle"
    :confirm-button-label="knowledgeContent.pdfSubmitLabel"
    :is-loading="isSaving"
    :disable-confirm-button="!selectedFile || isSaving"
    @confirm="handleConfirm"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ knowledgeContent.pdfDialogDescription }}
      </p>
    </template>

    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ knowledgeContent.pdfFileLabel }}
        </label>

        <div
          class="flex items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-3"
        >
          <div v-if="selectedFile" class="min-w-0">
            <p class="truncate text-sm text-n-slate-12">
              {{ selectedFile.name }}
            </p>
            <p class="text-xs text-n-slate-11">{{ selectedFileSizeLabel }}</p>
          </div>
          <span v-else class="text-sm text-n-slate-11">
            {{ knowledgeContent.pdfNoFileLabel }}
          </span>

          <div class="flex flex-shrink-0 items-center gap-1">
            <Button
              :label="
                selectedFile
                  ? knowledgeContent.pdfChangeFileLabel
                  : knowledgeContent.pdfChooseFileLabel
              "
              icon="i-lucide-upload"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleFileClick"
            />
            <Button
              v-if="selectedFile"
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleRemoveFile"
            />
          </div>
        </div>

        <input
          ref="fileInput"
          type="file"
          accept="application/pdf,.pdf"
          class="hidden"
          @change="handleFileChange"
        />
      </div>

      <Input
        :model-value="title"
        :label="knowledgeContent.titleLabel"
        :placeholder="knowledgeContent.titlePlaceholder"
        size="md"
        type="text"
        @update:model-value="handleTitleInput"
      />

      <div
        class="flex items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div class="min-w-0">
          <p class="text-sm font-medium text-n-slate-12">
            {{ knowledgeContent.activeLabel }}
          </p>
          <p class="mt-1 text-sm leading-6 text-n-slate-11">
            {{ knowledgeContent.activeDescription }}
          </p>
        </div>
        <Switch v-model="active" />
      </div>

      <div
        class="flex items-start gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
      >
        <span
          class="mt-0.5 size-4 flex-shrink-0 text-n-slate-10 i-lucide-info"
        />
        <p class="text-sm leading-6 text-n-slate-11">
          {{ knowledgeContent.pdfScannedWarning }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
